import React, { ChangeEvent, useEffect, useState } from 'react';
import { Formik, Field, Form, FormikHelpers } from 'formik';
import { ErrorMessage } from 'formik';
import { Alert, Box, Button, Checkbox, Container, FormControl, FormControlLabel, Grid, IconButton, InputLabel, MenuItem, Paper, Select, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { IoMdAddCircle } from 'react-icons/io';
import { TiDelete } from 'react-icons/ti';
import { SolicitudEstudianteItinerarioDTO, SetSolicitudEstudianteItinerarioDTO } from '../../interfaces/Itinerario/SolicitudEstudianteItinerarioDTO';
import ItinerarioService from '../../services/ItinerarioService';
import AlertService from '../../services/AlertService';
import { PeriodoAcademicoDTO, VoidPeriodoAcademicoDTO } from '../../interfaces/SAAC/PeriodoAcademicoDTO';
import { TblDecInvProyectoTesistaCronograma, VoidTblDecInvProyectoTesistaCronograma } from '../../interfaces/Itinerario/TblDecInvProyectoTesistaCronograma';
import { VoidTblDecInvProyectoTesistaObjespec } from '../../interfaces/Itinerario/TblDecInvProyectoTesistaObjespec';
import { SolicitudItinerarioValidate } from '../ValidationForm/validationForm';
import { addDays, format } from 'date-fns';
import { EstudianteActivoDTO } from '../../interfaces/SAAC/EstudianteActivoDTO';
import AcademicoService from '../../services/AcademicoService';
import { CatalogoProfesor } from '../../interfaces/SAAC/CatalogoProfesor';
import { MateriaInvestigacionDTO } from '../../interfaces/SAAC/MateriaDTO';

const alertService = new AlertService();
const itinerario = new ItinerarioService();
const academicoService = new AcademicoService();

interface Props {
    estudiante: EstudianteActivoDTO;
    idProyecto: number;
    SolicitudItinerario: SolicitudEstudianteItinerarioDTO;
    token: any;
    onResponse?: (dataRespuesta: any) => void; // Función para pasar la respuesta al padre
}

export const ItinerarioForm: React.FC<Props> = ({ SolicitudItinerario, estudiante, idProyecto, token, onResponse }) => {
    const navigate = useNavigate();
    const [periodoActivo, setPeriodoActivo] = useState<PeriodoAcademicoDTO>(VoidPeriodoAcademicoDTO());
    const [docentes, setDocentes] = useState<Array<CatalogoProfesor>>([]);
    const [materiasInv, setMateriasInv] = useState<Array<MateriaInvestigacionDTO>>([]);
    const [mensajes, setMensajes] = useState<Array<string>>([]);
    const [validateFechas, setValidateFechas] = useState(true);
    const [mensajeValidateFechas, setMensajeValidateFechas] = useState('');

    const GetPeriodo = async () => {
        await academicoService.GetPeriodoAcademicoEnEjecucion(token)
            .then((data) => {
                setPeriodoActivo(data.data)
                GetDocentes(data.data.idperiodo)
                GetMateriasInvestigacion();
            })
            .catch((error) => console.error(error));
    };

    const GetDocentes = async (idperiodo: number) => {
        await academicoService.GetCatalogoProfesores(token, idperiodo, idProyecto, 'I')
            .then((data) => {
                //console.log(data.listData);
                setDocentes(data.listData);
            })
            .catch((error) => console.error(error));
    };

    const GetMateriasInvestigacion = async () => {
        await academicoService.GetMateriaInvestigacionAp(token, estudiante.matricula)
            .then((data) => {
                //console.log(data.listData);
                setMateriasInv(data.listData);
            })
            .catch((error) => console.error(error));
    };

    const AgregarObjetivo = (setFieldValue: FormikHelpers<SolicitudEstudianteItinerarioDTO>['setFieldValue'], values: SolicitudEstudianteItinerarioDTO) => {
        const obj = VoidTblDecInvProyectoTesistaObjespec();
        setFieldValue('objespecificos', [...values.objespecificos, obj]);
    };

    const RemoverObjetivo = (setFieldValue: FormikHelpers<SolicitudEstudianteItinerarioDTO>['setFieldValue'], index: number, values: SolicitudEstudianteItinerarioDTO) => {
        const updatedObjetivos = [...values.objespecificos];
        updatedObjetivos.splice(index, 1);
        setFieldValue('objespecificos', updatedObjetivos);
    };

    const AgregarActividad = (setFieldValue: FormikHelpers<SolicitudEstudianteItinerarioDTO>['setFieldValue'], values: SolicitudEstudianteItinerarioDTO) => {
        const actividad = VoidTblDecInvProyectoTesistaCronograma(); // Inicializa la nueva actividad
        let fechaInicio = new Date(periodoActivo.fechainiclases); // Fecha de inicio basada en el período activo
        let fechaFin = addDays(fechaInicio, 7); // La fecha de fin es 7 días después de la fecha de inicio

        // Si ya existe alguna actividad en el cronograma, ajusta las fechas
        if (values.cronograma.length > 0) {
            const ultimaActividad = values.cronograma[values.cronograma.length - 1];
            fechaInicio = addDays(new Date(ultimaActividad.fechafin), 1); // La nueva actividad empieza un día después de la última actividad
            fechaFin = addDays(fechaInicio, 7); // La fecha de fin es 7 días después de la nueva fecha de inicio
        }

        actividad.fechainicio = fechaInicio; // Asigna la nueva fecha de inicio
        actividad.fechafin = fechaFin; // Asigna la nueva fecha de fin

        // Agrega la nueva actividad al array cronograma
        setFieldValue('cronograma', [...(values.cronograma || []), actividad]);
    };

    const RemoverActividad = (setFieldValue: FormikHelpers<SolicitudEstudianteItinerarioDTO>['setFieldValue'], index: number, values: SolicitudEstudianteItinerarioDTO) => {
        const updatedActividades = [...values.cronograma];
        updatedActividades.splice(index, 1);
        setFieldValue('cronograma', updatedActividades);
    };

    const GuardarSolicitudItinerario = async (values: SolicitudEstudianteItinerarioDTO) => {
        //console.log('Post', values);
        alertService.showLoading("Espere...");
        setMensajes([]);
        await itinerario.PostSolicitudItinerarioById(token, values)
            .then((data) => {
                if (data.status.isSuccessfully) {
                    if (onResponse) {
                        onResponse(data.data);
                    } else {
                        console.error('La función onResponse no está definida.');
                    }
                    alertService.hideLoading();
                    alertService.alertaExito('Éxito', 'Solicitud registrada.');
                } else {
                    let datos = data.status.messages[0].split('|');
                    setMensajes(datos);
                    alertService.hideLoading();
                    alertService.alertaAdvertenciaList('Alerta', datos);
                }
            })
            .catch((error) => console.error(error));
    };

    const ActualizarSolicitudItinerario = async (values: SolicitudEstudianteItinerarioDTO) => {
        //console.log('Post', values);
        alertService.showLoading("Espere...");
        setMensajes([]);
        await itinerario.UpdateSolicitudItinerarioById(token, values)
            .then((data) => {
                if (data.status.isSuccessfully) {
                    if (onResponse) {
                        onResponse(data.data);
                    } else {
                        console.error('La función onResponse no está definida.');
                    }
                    alertService.hideLoading();
                    alertService.alertaExito('Éxito', 'Solicitud actualizada.');
                } else {
                    setMensajes(data.status.messages);
                    alertService.hideLoading();
                    alertService.alertaAdvertenciaList('Alerta', data.status.messages);
                }
            })
            .catch((error) => console.error(error));
    };

    function normalizarFecha(fecha: string | Date): Date {
        let fechaObj: Date;

        // Verificar si es una cadena, convertirla a Date si es necesario
        if (typeof fecha === 'string') {
            console.log('string')
            const [year, month, day] = fecha.split('-').map(Number);
            fechaObj = new Date(year, month - 1, day);
        } else {
            console.log('date')
            fechaObj = fecha;
        }

        // Si la fecha no es válida (por ejemplo, cadena inválida), retorna null o lanza un error
        if (isNaN(fechaObj.getTime())) {
            throw new Error('Fecha no válida');
        }

        // Crear una nueva fecha con año, mes y día, ignorando las horas
        return new Date(fechaObj.getFullYear(), fechaObj.getMonth(), fechaObj.getDate());
    }

    function ValidarFechas(actividades: Array<TblDecInvProyectoTesistaCronograma>, periodoActivo: { fechainiclases: Date | null; fechafinclases: Date | null }): boolean {
        if (actividades.length === 0 || !periodoActivo.fechainiclases || !periodoActivo.fechafinclases) {
            return false; // No hay actividades o fechas del periodo no definidas
        }

        // Convertir fechas a objetos Date
        const fechaInicioPeriodo = new Date(periodoActivo.fechainiclases);
        const fechaFinPeriodo = new Date(periodoActivo.fechafinclases);

        // Ordenar actividades por fecha de inicio
        actividades.sort((a, b) => new Date(a.fechainicio).getTime() - new Date(b.fechainicio).getTime());

        const fechaInicioPrimeraActividad = normalizarFecha(actividades[0].fechainicio);
        const fechaFinUltimaActividad = normalizarFecha(actividades[actividades.length - 1].fechafin);
        // console.log(actividades[0].fechainicio);
        // console.log(fechaInicioPrimeraActividad);
        // console.log(actividades[actividades.length - 1].fechafin);
        // console.log(fechaFinUltimaActividad);


        // Validar que la primera actividad comience en la fecha de inicio del periodo
        if (fechaInicioPrimeraActividad.getTime() !== fechaInicioPeriodo.getTime()) {
            setMensajeValidateFechas('Primera actividad no comienza en el inicio del periodo');
            return false; // Primera actividad no comienza en el inicio del periodo
        }

        // Validar que la última actividad termine en la fecha de fin del periodo
        if (fechaFinUltimaActividad.getTime() !== fechaFinPeriodo.getTime()) {
            setMensajeValidateFechas('Última actividad no termina en el fin del periodo');
            return false; // Última actividad no termina en el fin del periodo
        }

        // Verificar que todas las actividades estén dentro del rango del periodo
        for (let i = 0; i < actividades.length; i++) {
            const fechaInicioActividad = normalizarFecha(actividades[i].fechainicio);
            const fechaFinActividad = normalizarFecha(actividades[i].fechafin);

            // Validar que cada actividad esté dentro del periodo
            if (fechaInicioActividad < fechaInicioPeriodo || fechaFinActividad > fechaFinPeriodo) {
                let n = i + 1;
                setMensajeValidateFechas('Actividad #' + n + ' fuera del rango del periodo')
                return false; // Actividad fuera del rango del periodo
            }
        }

        // Si todas las validaciones pasaron, devolver true
        return true;
    }

    useEffect(() => {
        GetPeriodo();
    }, [estudiante]);

    return (
        <Container maxWidth="lg" style={{ border: '1px solid #517ED3', borderRadius: '5px', padding: '20px', textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom style={{ fontWeight: 'bold', paddingBottom: '1%', color: '#517ED3' }}>
                Detalle de la Solicitud
            </Typography>
            <Formik
                initialValues={SolicitudItinerario}
                validationSchema={SolicitudItinerarioValidate}
                onSubmit={(values, { setSubmitting }) => {
                    setMensajeValidateFechas('');
                    setValidateFechas(true);
                    const bandera = ValidarFechas(values.cronograma, periodoActivo);
                    if (bandera) {
                        const obj = SetSolicitudEstudianteItinerarioDTO(values);
                        obj.itinerario.anio = periodoActivo.anio;
                        obj.itinerario.termino = periodoActivo.termino;
                        obj.itinerario.estado = 'P';
                        obj.itinerario.codEstudiante = estudiante.matricula;
                        obj.itinerario.codCarrera = estudiante.codcarrera;
                        const docente = docentes.find(x => x.email === obj.itinerario.mailtutor);
                        obj.itinerario.nombretutor = docente?.label ?? '';
                        obj.itinerario.iddecinvproyecto = idProyecto
                        console.log(obj);
                        if (obj.itinerario.iddecinvproyectotesista === 0) {
                            GuardarSolicitudItinerario(obj);
                        } else {
                            ActualizarSolicitudItinerario(obj);
                        }
                    } else {
                        const texto = `Revise las fechas de las actividades deben estar dentro del rango: inicio ${periodoActivo.fechainiclases ? format(new Date(periodoActivo.fechainiclases), 'dd/MMM/yyyy') : "Fecha no disponible"} y final ${periodoActivo.fechafinclases ? format(new Date(periodoActivo.fechafinclases), 'dd/MMM/yyyy') : "Fecha no disponible"}`;
                        alertService.alertaAdvertencia("Alerta", texto)
                        setValidateFechas(false);
                    }
                    setSubmitting(false);
                }}
            >
                {({ values, setFieldValue, errors, touched, handleSubmit }) => (
                    <Form onSubmit={handleSubmit}>
                        <Grid container spacing={2}>
                            {/* Tema del Proyecto */}
                            <Grid item xs={12}>
                                <Field
                                    as={TextField}
                                    fullWidth
                                    id="itinerario.temainv"
                                    name="itinerario.temainv"
                                    label="Tema del Proyecto"
                                    error={touched.itinerario?.temainv && Boolean(errors.itinerario?.temainv)}
                                />
                                <ErrorMessage name="itinerario.temainv" className='errorMessage' component="div">
                                    {(error) => (
                                        <Alert severity="error">
                                            {error}
                                        </Alert>
                                    )}
                                </ErrorMessage>
                            </Grid>
                            {/* Materia */}
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                    <InputLabel htmlFor="select-cod-materia">Materia</InputLabel>
                                    <Field
                                        as={Select}
                                        labelId="select-cod-materia"
                                        label="Materia"
                                        id="itinerario.codigomateria"
                                        name="itinerario.codigomateria"
                                        error={touched.itinerario?.codigomateria && Boolean(errors.itinerario?.codigomateria)}
                                    >
                                        {materiasInv.length > 0 ? (
                                            materiasInv.map((materia) => (
                                                <MenuItem key={materia.codmateria} value={materia.codmateria}>
                                                    {materia.nombremateria}
                                                </MenuItem>
                                            ))
                                        ) : (
                                            <MenuItem value="">Seleccione una Materia</MenuItem>
                                        )}
                                    </Field>
                                    <ErrorMessage name="itinerario.codigomateria" className='errorMessage' component="div">
                                        {(error) => (
                                            <Alert severity="error">
                                                {error}
                                            </Alert>
                                        )}
                                    </ErrorMessage>
                                </FormControl>
                            </Grid>

                            {/* Nombre del Tutor */}
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                    <InputLabel htmlFor="select-cod-docente">Tutor</InputLabel>
                                    <Field
                                        as={Select}

                                        labelId="select-cod-docente"
                                        id="itinerario.mailtutor"
                                        name="itinerario.mailtutor"
                                        label="Tutor"
                                        error={touched.itinerario?.mailtutor && Boolean(errors.itinerario?.mailtutor)}
                                    >
                                        {docentes.length > 0 ? (
                                            docentes.map((docente) => (
                                                <MenuItem key={docente.email} value={docente.email}>
                                                    {docente.label}
                                                </MenuItem>
                                            ))
                                        ) : (
                                            <MenuItem value="">Seleccione un docente</MenuItem>
                                        )}
                                    </Field>
                                    <ErrorMessage name="itinerario.mailtutor" className='errorMessage' component="div">
                                        {(error) => (
                                            <Alert severity="error">
                                                {error}
                                            </Alert>
                                        )}
                                    </ErrorMessage>
                                </FormControl>
                            </Grid>

                            {/* Objetivo General */}
                            <Grid item xs={12}>
                                <Field
                                    as={TextField}
                                    fullWidth
                                    id="itinerario.objgeneral"
                                    name="itinerario.objgeneral"
                                    label="Objetivo General"
                                    multiline
                                    rows={3}
                                    error={touched.itinerario?.objgeneral && Boolean(errors.itinerario?.objgeneral)}
                                />
                                <ErrorMessage name="itinerario.objgeneral" className='errorMessage' component="div">
                                    {(error) => (
                                        <Alert severity="error">
                                            {error}
                                        </Alert>
                                    )}
                                </ErrorMessage>
                            </Grid>

                            {/* Término */}
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    id="itinerario.termino"
                                    name="itinerario.termino"
                                    label="Término"
                                    value={periodoActivo.termino || ""}
                                    disabled
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    id="itinerario.anio"
                                    name="itinerario.anio"
                                    label="Año"
                                    value={periodoActivo.anio || ""}
                                    disabled
                                />
                            </Grid>

                            {/* Objetivos Específicos */}
                            <Grid item xs={12}>
                                <Paper elevation={3} style={{ padding: '16px', border: '1px solid #517ED3' }}>
                                    <Box display="flex" justifyContent="space-between" alignItems="center">
                                        <Typography variant="body1" style={{ fontWeight: 'bold', color: '#517ED3' }}>
                                            Objetivos Específicos
                                        </Typography>
                                        <IconButton aria-label="agregar" color="info" onClick={() => AgregarObjetivo(setFieldValue, values)}>
                                            <IoMdAddCircle />
                                        </IconButton>
                                    </Box>
                                </Paper>
                            </Grid>

                            {/* Tabla de Objetivos Específicos */}
                            <Grid item xs={12}>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>#</TableCell>
                                            <TableCell>Objetivo</TableCell>
                                            <TableCell>Eliminar</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {values.objespecificos && values.objespecificos.length > 0 ? (
                                            values.objespecificos.map((obj, index) => (
                                                <TableRow key={`obj-${index}`}>
                                                    <TableCell>{index + 1}</TableCell>
                                                    <TableCell>
                                                        <Field
                                                            as={TextField}
                                                            fullWidth
                                                            name={`objespecificos[${index}].objespec`}
                                                            value={obj.objespec}
                                                        />
                                                        <ErrorMessage name={`objespecificos[${index}].objespec`} className='errorMessage' component="div">
                                                            {(error) => (
                                                                <Alert severity="error">
                                                                    {error}
                                                                </Alert>
                                                            )}
                                                        </ErrorMessage>
                                                    </TableCell>
                                                    <TableCell>
                                                        {obj.iddecinvproyectotesistaobjespec === 0 ?
                                                            <IconButton aria-label="eliminar" color="error" onClick={() => RemoverObjetivo(setFieldValue, index, values)}>
                                                                <TiDelete />
                                                            </IconButton>
                                                            :
                                                            <></>}
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        ) : (
                                            <></>
                                        )}
                                    </TableBody>
                                </Table>
                                <ErrorMessage name={`objespecificos`} className='errorMessage' component="div">
                                    {(error) => (
                                        <>
                                            {errors.objespecificos && touched.objespecificos && typeof errors.objespecificos === 'string' && (
                                                <Alert severity="error">
                                                    <div>{errors.objespecificos}</div>
                                                </Alert>
                                            )}
                                        </>
                                    )}
                                </ErrorMessage>
                            </Grid>

                            {/* Actividades del Cronograma */}
                            <Grid item xs={12}>
                                <Paper elevation={3} style={{ padding: '16px', border: '1px solid #517ED3' }}>
                                    <Box display="flex" justifyContent="space-between" alignItems="center">
                                        <Typography variant="body1" style={{ fontWeight: 'bold', color: '#517ED3' }}>
                                            Actividades del Cronograma
                                        </Typography>
                                        <IconButton aria-label="agregar" color="info" onClick={() => AgregarActividad(setFieldValue, values)}>
                                            <IoMdAddCircle />
                                        </IconButton>
                                    </Box>
                                </Paper>
                            </Grid>

                            {/* Tabla de Actividades del Cronograma */}
                            <Grid item xs={12}>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>#</TableCell>
                                            <TableCell>Actividad</TableCell>
                                            <TableCell>Fecha Inicio</TableCell>
                                            <TableCell>Fecha Fin</TableCell>
                                            <TableCell>¿Requiere Presencialidad?</TableCell>
                                            <TableCell>Eliminar</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {values.cronograma && values.cronograma.length > 0 ? (
                                            values.cronograma.map((act, index) => (
                                                <TableRow key={`act-${index}`}>
                                                    <TableCell>{index + 1}</TableCell>
                                                    <TableCell>
                                                        <Field
                                                            as={TextField}
                                                            fullWidth
                                                            name={`cronograma[${index}].actividad`}
                                                            value={act.actividad}
                                                        />
                                                        <ErrorMessage name={`cronograma[${index}].actividad`} className='errorMessage' component="div">
                                                            {(error) => (
                                                                <Alert severity="error">
                                                                    {error}
                                                                </Alert>
                                                            )}
                                                        </ErrorMessage>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Field
                                                            as={TextField}
                                                            type="date"
                                                            name={`cronograma[${index}].fechainicio`}
                                                            value={new Date(act.fechainicio).toISOString().slice(0, 10)}
                                                            fullWidth
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Field
                                                            as={TextField}
                                                            type="date"
                                                            name={`cronograma[${index}].fechafin`}
                                                            value={new Date(act.fechafin).toISOString().slice(0, 10)}
                                                            fullWidth
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <FormControlLabel
                                                            control={
                                                                <Field
                                                                    as={Checkbox}
                                                                    name={`cronograma[${index}].presencial`}
                                                                    checked={act.presencial === 1}
                                                                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                                                        const isChecked = e.target.checked;
                                                                        // Cambia el valor de act.presencial a 1 si está marcado, de lo contrario 0
                                                                        setFieldValue(`cronograma[${index}].presencial`, isChecked ? 1 : 0);
                                                                    }}
                                                                />
                                                            }
                                                            label="Sí"
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        {act.iddecinvproyectotesistacronograma === 0 ?
                                                            <IconButton aria-label="eliminar" color="error" onClick={() => RemoverActividad(setFieldValue, index, values)}>
                                                                <TiDelete />
                                                            </IconButton>
                                                            : <></>
                                                        }
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        ) : (
                                            <></>
                                        )}
                                    </TableBody>
                                </Table>
                                <ErrorMessage name={`cronograma`} className='errorMessage' component="div">
                                    {(error) => (
                                        <>
                                            {errors.cronograma && touched.cronograma && typeof errors.cronograma === 'string' && (
                                                <Alert severity="error">
                                                    <div>{errors.cronograma}</div>
                                                </Alert>
                                            )}
                                        </>
                                    )}
                                </ErrorMessage>
                            </Grid>
                            <Grid item xs={12}>
                                {validateFechas ?
                                    <></>
                                    :
                                    <Alert severity="error">
                                        <div>Revise las fechas de las actividades deben estar dentro del rango: inicio {periodoActivo.fechainiclases ? format(new Date(periodoActivo.fechainiclases), 'dd/MMM/yyyy') : "Fecha no disponible"} y final {periodoActivo.fechafinclases ? format(new Date(periodoActivo.fechafinclases), 'dd/MMM/yyyy') : "Fecha no disponible"}</div>
                                        <div>{mensajeValidateFechas}</div>
                                    </Alert>
                                }
                            </Grid>
                            {/* Botón Guardar */}
                            <Grid item xs={12}>
                                <Button variant="contained" type="submit" style={{ backgroundColor: '#517ED3', color: 'white' }} >
                                    GUARDAR
                                </Button>
                            </Grid>
                        </Grid>
                    </Form>
                )}
            </Formik>
            <br />
            {mensajes.map((mensaje, i) => (
                <div key={"mensajeError-" + i} style={{ color: 'red' }}>
                    {mensaje.length > 0 ? <><strong>{i + 1}.</strong> {mensaje} </> : <></>}
                </div>
            ))}
        </Container>
    );
};
